import React from 'react';
import { FIELD, LABEL, FIELD_ERROR } from './formClasses';

/**
 * Server card tool filter control.
 *
 * The card's `tool_filter` is a regex allowlist or denylist over tool names.
 * It applies to every caller, administrators included, so the copy says so
 * explicitly: an operator who reads this as "per-user visibility" will set a
 * policy that hides a tool from themselves too.
 */

export type ToolFilterMode = 'none' | 'allow' | 'deny';

export interface ToolFilterValue {
  mode: 'allow' | 'deny';
  patterns: string[];
}

/**
 * Validate the textarea contents and build the request value.
 *
 * The regex check here is a convenience only. The server revalidates every
 * pattern and additionally enforces structural safety rules this cannot see
 * (no quantifier on a group, no backreferences, a cap on stacked unbounded
 * quantifiers), so a pattern accepted here can still be rejected with a 422.
 * The server is the control; callers must surface its message rather than
 * treating this function's success as approval.
 *
 * @param mode Selected filter mode.
 * @param raw Newline-separated pattern text.
 * @returns The value to send (null for no policy) and a client-side error.
 */
export function buildToolFilter(
  mode: ToolFilterMode,
  raw: string,
): { value: ToolFilterValue | null; error: string | null } {
  if (mode === 'none') {
    return { value: null, error: null };
  }

  const patterns = raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (patterns.length === 0) {
    return { value: null, error: 'Enter at least one pattern, or choose "No filter".' };
  }

  for (const pattern of patterns) {
    try {
      // eslint-disable-next-line no-new
      new RegExp(pattern);
    } catch {
      return { value: null, error: `Not a valid regular expression: ${pattern}` };
    }
  }

  return { value: { mode, patterns }, error: null };
}

interface ToolFilterFieldProps {
  mode: ToolFilterMode;
  patterns: string;
  onModeChange: (mode: ToolFilterMode) => void;
  onPatternsChange: (patterns: string) => void;
  /** Client-side or server-side rejection message shown under the textarea. */
  error?: string | null;
}

const ToolFilterField: React.FC<ToolFilterFieldProps> = ({
  mode,
  patterns,
  onModeChange,
  onPatternsChange,
  error,
}) => {
  return (
    <div className="space-y-2">
      <label className={LABEL}>Tool filter</label>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Applies to every caller, including administrators. Patterns must match the
        full tool name.
      </p>
      <select
        value={mode}
        onChange={(e) => onModeChange(e.target.value as ToolFilterMode)}
        className={FIELD}
      >
        <option value="none">No filter</option>
        <option value="allow">Allowlist - expose only matching tools</option>
        <option value="deny">Denylist - hide matching tools</option>
      </select>
      {mode !== 'none' && (
        <>
          <textarea
            value={patterns}
            onChange={(e) => onPatternsChange(e.target.value)}
            rows={5}
            placeholder={'One regular expression per line, for example:\ndelete_.*\ndrop_table'}
            className={`${FIELD} font-mono text-sm`}
          />
          {error && <p className={FIELD_ERROR}>{error}</p>}
        </>
      )}
    </div>
  );
};

export default ToolFilterField;
